import type { AppUser } from 'common/models';
import HeaderItems from 'components/Header/HeaderItems';
import { useIsCustomFormsTableEnabled } from 'helpers/featureFlagsHelper';
import { useCustomTranslation } from 'helpers/translationHelper';
import { isOnlyTechnician } from 'helpers/userRoles';
import React from 'react';

interface Props {
  user: AppUser;
  hideStores?: boolean;
  restrictTechnicianDataVisibility?: boolean;
}

export const useUserNavigationItems = (props: Props) => {
  const { user, hideStores, restrictTechnicianDataVisibility } = props;

  const { t } = useCustomTranslation();
  const isCustomFormsEnabled = useIsCustomFormsTableEnabled();

  const isManager = user.isManager || user.isAdmin;
  const isReadonly = user.isReadonly;
  const canSeeTechnicianData = isManager || isReadonly || !restrictTechnicianDataVisibility;

  const items = [
    {
      to: '/',
      label: t('mainMenu.schedule'),
      exact: true
    },
    {
      to: '/orders',
      label: t('mainMenu.orders')
    },
    {
      to: '/interventions',
      label: t('mainMenu.interventions')
    },
    {
      to: '/customForms',
      label: t('mainMenu.customForms'),
      hidden: !isCustomFormsEnabled || isOnlyTechnician(user)
    },
    {
      to: '/request',
      label: t('mainMenu.orderRequests'),
      hidden: !(isManager || isReadonly)
    },
    {
      to: '/customers',
      inactiveRoutes: ['^/customers/.+/serviceObjects/.+$'],
      label: t('mainMenu.customers'),
      hidden: !canSeeTechnicianData
    },
    {
      to: '/serviceObjects',
      activeRoutes: ['^/serviceObjects(?:/|$)', '^/customers/.+/serviceObjects/.+$'],
      label: t('mainMenu.serviceObjects'),
      hidden: !canSeeTechnicianData
    },
    {
      to: '/material',
      label: t('mainMenu.material'),
      hidden: !!hideStores
    },
    {
      to: '/dashboard',
      label: t('mainMenu.dashboard'),
      hidden: !user.isReportingEnabled
    },
    {
      to: '/settings',
      label: t('mainMenu.settings'),
      hidden: !user.isAdmin
    }
  ];

  return items;
};

const UserHeader: React.FC<Props> = (props): React.ReactElement => {
  const items = useUserNavigationItems(props);
  return <HeaderItems items={items} />;
};

export default UserHeader;
